import { Box } from '@mantine/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const OPEN_DELAY = 300;
const GAP = 6;
const VIEWPORT_MARGIN = 8;
const MAX_WIDTH = 360;

interface TooltipState {
  text: string;
  rect: DOMRect;
}

const isTruncated = (element: HTMLElement) =>
  element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1;

/**
 * Jedan globalni tooltip za sav tekst skraćen s "…" (Mantine `truncate` → `data-truncate`,
 * `lineClamp` → `data-line-clamp`): na hover mišem prikazuje cijeli tekst, ali samo kad stvarno
 * ne stane u ćeliju. Elementi koji već imaju vlastiti tooltip označeni su s `data-own-tooltip`.
 */
export const TruncationTooltip = () => {
  const [state, setState] = useState<TooltipState | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: number | undefined;
    let current: HTMLElement | null = null;

    const hide = () => {
      window.clearTimeout(timer);
      current = null;
      setState(null);
      setPosition(null);
    };

    const handleOver = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(
        '[data-truncate], [data-line-clamp]'
      );
      if (target === current) return;
      hide();
      if (!target || target.closest('[data-own-tooltip]')) return;
      current = target;
      timer = window.setTimeout(() => {
        const text = target.textContent?.trim();
        if (current !== target || !text || !isTruncated(target)) return;
        setState({ text, rect: target.getBoundingClientRect() });
      }, OPEN_DELAY);
    };

    const handleOut = (event: PointerEvent) => {
      if (!current) return;
      const next = event.relatedTarget as Node | null;
      if (next && current.contains(next)) return;
      hide();
    };

    document.addEventListener('pointerover', handleOver);
    document.addEventListener('pointerout', handleOut);
    document.addEventListener('pointerdown', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerover', handleOver);
      document.removeEventListener('pointerout', handleOut);
      document.removeEventListener('pointerdown', hide);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!state || !tooltip) return;
    const { width, height } = tooltip.getBoundingClientRect();
    const fitsAbove = state.rect.top - GAP - height >= VIEWPORT_MARGIN;
    const top = fitsAbove ? state.rect.top - GAP - height : state.rect.bottom + GAP;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(state.rect.left, maxLeft));
    setPosition({ top, left });
  }, [state]);

  if (!state) return null;

  return createPortal(
    <Box
      ref={tooltipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
        zIndex: 1000,
        maxWidth: MAX_WIDTH,
        padding: '6px 10px',
        fontSize: 13,
        lineHeight: 1.4,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        pointerEvents: 'none',
        color: 'var(--color-text-primary)',
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-soft)',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      {state.text}
    </Box>,
    document.body
  );
};
